import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { Grid, Pagination, Loader } from 'semantic-ui-react';
import { PageContainer } from '../../components/pageContainer/PageContainer';
import { Panel } from '../../components/panel/Panel';
import { useAppStateFeedSelector, feedActions } from './feedSlice';
import { loadNewsPosts, loadEventsPosts, loadCategories, loadSources } from './feedActionCreators';
import { FeedCard } from './components/FeedCard';
import { FilterPanel } from './components/FilterPanel';
import { FeedLoader } from './components/FeedLoader';
import { FeedUnavailable } from './components/FeedUnavailable';
import { useFeatureFlag, useFeatureFlagsSettled, useFeatureFlagsErrored } from '../featureFlags/FeatureFlag';
import { FeedFilters } from '../../models/feed';
import styles from './FeedList.module.scss';

interface FeedListProps {
    type: 'news' | 'event';
}

export const FeedList: React.FC<FeedListProps> = ({ type }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const newsAndEventsFF = useFeatureFlag('EnableNewsAndEventsFeed');
    const featureFlagsSettled = useFeatureFlagsSettled();
    const featureFlagsErrored = useFeatureFlagsErrored();

    const {
        newsPosts,
        newsLoading,
        newsPage,
        newsTotalPages,
        eventsPosts,
        eventsLoading,
        eventsPage,
        eventsTotalPages,
        categories,
        sources,
    } = useAppStateFeedSelector((state) => state);

    const [filters, setFilters] = useState<FeedFilters>(
        type === 'event' ? { onlyEvents: true } : {}
    );

    useEffect(() => {
        // Load categories and sources
        dispatch(loadCategories());
        dispatch(loadSources());
    }, [dispatch]);

    useEffect(() => {
        // Load posts when filters or page changes
        const page = type === 'news' ? newsPage : eventsPage;
        if (type === 'news') {
            dispatch(loadNewsPosts(page, 20, filters));
        } else {
            dispatch(loadEventsPosts(page, 20, filters));
        }
    }, [dispatch, type, filters, newsPage, eventsPage]);

    // Hold the route until the flags settle. Redirecting on an unresolved flag
    // bounced direct hits on /news and /events to the homepage, because a cold
    // page load renders this before the flags request comes back.
    if (!featureFlagsSettled) {
        return (
            <FeedLoader
                variant={type}
                heading={type === 'news' ? t('Feed.News') : t('Feed.Events')}
                label={
                    type === 'news'
                        ? t('Feed.LoadingNews')
                        : t('Feed.LoadingEvents')
                }
            />
        );
    }

    // A failed flags request means we do not know whether the feed is enabled.
    // Redirecting on that is indistinguishable from a broken link.
    if (featureFlagsErrored) {
        return <FeedUnavailable />;
    }

    if (!newsAndEventsFF?.enabled) {
        return <Redirect to="/" />;
    }

    const posts = type === 'news' ? newsPosts : eventsPosts;
    const loading = type === 'news' ? newsLoading : eventsLoading;
    const currentPage = type === 'news' ? newsPage : eventsPage;
    const totalPages = type === 'news' ? newsTotalPages : eventsTotalPages;

    const handlePageChange = (e: any, data: any) => {
        const loadAction = type === 'news' ? loadNewsPosts : loadEventsPosts;
        dispatch(loadAction(data.activePage, 20, filters));
    };

    const handleFilterChange = (newFilters: FeedFilters) => {
        // Reset to page 1 when filters change
        if (type === 'news') {
            dispatch(feedActions.setNewsPage(1));
        } else {
            dispatch(feedActions.setEventsPage(1));
        }
        setFilters(newFilters);
    };

    const handleClearFilters = () => {
        // Reset to page 1 when clearing filters
        if (type === 'news') {
            dispatch(feedActions.setNewsPage(1));
        } else {
            dispatch(feedActions.setEventsPage(1));
        }
        setFilters(type === 'event' ? { onlyEvents: true } : {});
    };

    return (
        <PageContainer>
            <Panel>
                <h1 className={styles.pageTitle}>
                    {type === 'news' ? t('Feed.News') : t('Feed.Events')}
                </h1>
                <FilterPanel
                    filters={filters}
                    categories={categories}
                    sources={sources}
                    onFilterChange={handleFilterChange}
                    onClearFilters={handleClearFilters}
                    type={type}
                />
                {loading ? (
                    <div className={styles.loaderContainer}>
                        <Loader active inline="centered">
                            {type === 'news'
                                ? t('Feed.LoadingNews')
                                : t('Feed.LoadingEvents')}
                        </Loader>
                    </div>
                ) : !posts || posts.length === 0 ? (
                    <div className={styles.emptyState}>
                        <p>
                            {type === 'news'
                                ? t('Feed.NoNewsFound')
                                : t('Feed.NoEventsFound')}
                        </p>
                    </div>
                ) : (
                    <>
                        <Grid stackable columns={4} className={styles.postsGrid}>
                            {posts.map((post) => (
                                <Grid.Column key={post.id} className={styles.cardColumn}>
                                    <FeedCard post={post} />
                                </Grid.Column>
                            ))}
                        </Grid>
                        {totalPages > 1 && (
                            <div className={styles.paginationContainer}>
                                <Pagination
                                    activePage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={handlePageChange}
                                    boundaryRange={1}
                                    siblingRange={1}
                                    ellipsisItem={null}
                                    firstItem={null}
                                    lastItem={null}
                                    prevItem={{ content: '‹', icon: true }}
                                    nextItem={{ content: '›', icon: true }}
                                />
                            </div>
                        )}
                    </>
                )}
            </Panel>
        </PageContainer>
    );
};
